export const WORKOUT_READY_STATUS_LABEL = "Подготовлена";

/**
 * @param {Record<string, unknown>|null|undefined} day
 */
export function buildWorkoutOverviewStatusSourceFromWeekDay(day) {
  if (!day || typeof day !== "object") {
    return null;
  }

  const needsWeights = day.prescription_needs_weights ?? day.workout_prescription_needs_weights;
  const weightsComplete = day.prescription_weights_complete ?? day.workout_prescription_weights_complete;

  return {
    ent_is_completed: Boolean(day.ent_is_completed),
    ent_is_started: Boolean(day.ent_is_started),
    workout_exercises_confirmed: Boolean(day.workout_exercises_confirmed),
    workout_prescription_weights_confirmed: Boolean(day.workout_prescription_weights_confirmed),
    workout_sets_confirmed: Boolean(day.workout_sets_confirmed),
    workout_analysis_confirmed: Boolean(day.workout_analysis_confirmed),
    workout_result_checked: Boolean(day.workout_result_checked),
    workout_has_remarks: Boolean(day.workout_has_remarks),
    prescription_needs_weights: Boolean(needsWeights),
    prescription_weights_complete: Boolean(weightsComplete),
    workout_status: day.workout_status ?? null,
    cancel_reason: day.cancel_reason ?? null,
    status: day.status ?? null,
  };
}

/**
 * Статус тренировки в шапке страницы /workouts/[uuid].
 *
 * @param {{
 *   ent_is_completed?: boolean,
 *   ent_is_started?: boolean,
 *   workout_exercises_confirmed?: boolean,
 *   workout_prescription_weights_confirmed?: boolean,
 *   workout_sets_confirmed?: boolean,
 *   prescription_needs_weights?: boolean,
 *   prescription_weights_complete?: boolean,
 *   workout_status?: string|null,
 *   cancel_reason?: string|null,
 *   status?: string|null,
 * }|null|undefined} source
 * @returns {{
 *   label: string,
 *   statusBadge: { label: string, className: string },
 *   badge: { label: string, className: string } | null,
 *   defaultTab: string,
 * }}
 */
export function resolveWorkoutOverviewStatus(source) {
  if (isWorkoutCancelled(source)) {
    return {
      label: "Отменена",
      statusBadge: buildStatusBadge("Отменена", "cancelled"),
      badge: null,
      defaultTab: "exercises",
    };
  }

  if (!source) {
    return {
      label: "Черновик",
      statusBadge: buildStatusBadge("Черновик", "draft"),
      badge: null,
      defaultTab: "exercises",
    };
  }

  if (source.ent_is_completed) {
    return {
      label: "Завершена",
      statusBadge: buildStatusBadge("Завершена", "completed"),
      badge: null,
      defaultTab: "summary",
    };
  }

  if (source.ent_is_started) {
    return {
      label: "Выполняется",
      statusBadge: buildStatusBadge("Выполняется", "inProgress"),
      badge: null,
      defaultTab: "fact",
    };
  }

  if (source.workout_sets_confirmed) {
    return {
      label: WORKOUT_READY_STATUS_LABEL,
      statusBadge: buildStatusBadge(WORKOUT_READY_STATUS_LABEL, "ready"),
      badge: null,
      defaultTab: "plan",
    };
  }

  const badge = resolveWorkoutDraftStatusBadge(source);

  return {
    label: "Черновик",
    statusBadge: buildStatusBadge("Черновик", "draft"),
    badge,
    defaultTab: resolveWorkoutDraftDefaultTab(source),
  };
}

function resolveWorkoutDraftDefaultTab(source) {
  if (!source.workout_exercises_confirmed) {
    return "exercises";
  }

  if (!source.workout_prescription_weights_confirmed) {
    return "workout-plan";
  }

  if (!source.workout_sets_confirmed) {
    return "plan";
  }

  return "exercises";
}

function resolveWorkoutDraftStatusBadge(source) {
  if (!source.workout_exercises_confirmed) {
    return buildDraftBadge("Добавьте и подтвердите упражнения");
  }

  if (!source.workout_prescription_weights_confirmed) {
    if (source.prescription_needs_weights && !source.prescription_weights_complete) {
      return buildDraftBadge("Укажите нагрузку");
    }
    return buildDraftBadge("Подтвердите нагрузку");
  }

  if (!source.workout_sets_confirmed) {
    return buildDraftBadge("Подтвердите подходы");
  }

  return null;
}

const STATUS_BADGE_CLASSES = {
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
  inProgress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  ready: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200",
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  cancelled: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

export const WORKOUT_OVERVIEW_STATUS_FILTERS = [
  { value: "draft", shortLabel: "Черновик" },
  { value: "ready", shortLabel: WORKOUT_READY_STATUS_LABEL },
  { value: "inProgress", shortLabel: "Выполняется" },
  { value: "completed", shortLabel: "Завершена" },
  { value: "cancelled", shortLabel: "Отменена" },
];

/**
 * @param {Record<string, unknown>|null|undefined} workout
 */
export function isWorkoutMissingSlotWorkout(workout) {
  if (!workout || typeof workout !== "object") {
    return false;
  }

  return workout.status === "missing_slot" || Boolean(workout.is_missing_slot);
}

/**
 * Статус пустого слота недели в UI: «Черновик» (для тренера слот уже запланирован).
 *
 * @returns {ReturnType<typeof resolveWorkoutOverviewStatus>}
 */
export function resolveMissingSlotOverviewStatusView() {
  return {
    label: "Черновик",
    statusBadge: buildStatusBadge("Черновик", "draft"),
    badge: buildDraftBadge("Упражнения не добавлены"),
    defaultTab: "exercises",
  };
}

/**
 * Вариант для фильтрации и легенды: пустые слоты входят в «Черновик».
 *
 * @param {Record<string, unknown>|null|undefined} workout
 * @param {(workout: Record<string, unknown>) => Record<string, unknown>|null|undefined} [buildSourceFromWorkout]
 */
export function resolveWorkoutOverviewStatusFilterVariant(workout, buildSourceFromWorkout) {
  if (isWorkoutCancelled(workout)) {
    return "cancelled";
  }

  if (isWorkoutMissingSlotWorkout(workout)) {
    return "draft";
  }

  const source = buildSourceFromWorkout ? buildSourceFromWorkout(workout) : null;
  return resolveWorkoutOverviewStatusVariant(source);
}

/**
 * @param {Record<string, unknown>|null|undefined} workout
 */
export function isWorkoutCancelled(workout) {
  if (!workout || typeof workout !== "object") {
    return false;
  }

  const workoutStatus = String(workout.workout_status ?? "").trim().toLowerCase();
  if (workoutStatus === "cancelled") {
    return true;
  }

  const planStatus = String(workout.status ?? "").trim().toLowerCase();
  if (planStatus === "cancelled") {
    return true;
  }

  return Boolean(String(workout.cancel_reason ?? "").trim());
}

/**
 * @param {Record<string, unknown>|null|undefined} source
 */
export function resolveWorkoutOverviewStatusVariant(source) {
  if (isWorkoutCancelled(source)) {
    return "cancelled";
  }

  if (!source) {
    return "draft";
  }

  if (source.ent_is_completed) {
    return "completed";
  }

  if (source.ent_is_started) {
    return "inProgress";
  }

  if (source.workout_sets_confirmed) {
    return "ready";
  }

  return "draft";
}

function buildStatusBadge(label, variant) {
  return {
    label,
    className: STATUS_BADGE_CLASSES[variant] || STATUS_BADGE_CLASSES.draft,
  };
}

function buildDraftBadge(label) {
  return {
    label,
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
  };
}
